/** Card + filter pieces shared by Home, Kanban, Lineup and Planner. */
import { useMemo } from 'react';
import { DIVISIONS, TYPES, type Settings } from './config';
import { ageLabel, daysInStage, type Req } from './lib/data';
import { Divisions, StatusTag, TierBadge } from './ui';

export interface Filter { division: string; type: string; requester: string }
export const NO_FILTER: Filter = { division: '', type: '', requester: '' };

export function applyFilter(items: Req[], f: Filter) {
  return items
    .filter((r) => !f.division || r.divisions.includes(f.division as never) || (f.division !== 'all' && r.divisions.includes('all')))
    .filter((r) => !f.type || r.type === f.type)
    .filter((r) => !f.requester || r.requesterName === f.requester);
}
export const isFiltered = (f: Filter) => !!(f.division || f.type || f.requester);

export function Filters({ items, value, onChange }: { items: Req[]; value: Filter; onChange: (f: Filter) => void }) {
  const requesters = useMemo(() => [...new Set(items.map((r) => r.requesterName))].sort(), [items]);
  return (
    <div className="filters">
      <select value={value.division} onChange={(e) => onChange({ ...value, division: e.target.value })}>
        <option value="">All divisions</option>
        {DIVISIONS.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
      </select>
      <select value={value.type} onChange={(e) => onChange({ ...value, type: e.target.value })}>
        <option value="">All types</option>
        {TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
      </select>
      <select value={value.requester} onChange={(e) => onChange({ ...value, requester: e.target.value })}>
        <option value="">Anyone asking</option>
        {requesters.map((n) => <option key={n} value={n}>{n}</option>)}
      </select>
      {isFiltered(value) && <button className="linkish" onClick={() => onChange(NO_FILTER)}>clear</button>}
    </div>
  );
}

/** Compact request card: title, sub-stage, divisions, score, age / time-in-stage. */
export function Card({ r, settings, onOpen, showStatus = true, extra, stuckAfter = 7 }: {
  r: Req; settings: Settings; onOpen: (id: string) => void; showStatus?: boolean;
  extra?: React.ReactNode; stuckAfter?: number;
}) {
  const stuck = r.status !== 'completed' && r.status !== 'new' && daysInStage(r) > stuckAfter;
  return (
    <div className={`kcard ${stuck ? 'stuck' : ''}`}>
      <div className="kcard-title" onClick={() => onOpen(r.id)}>{r.title}</div>
      <div className="kcard-tags">
        {showStatus && <StatusTag status={r.status} />}
        <TierBadge r={r} settings={settings} />
      </div>
      <div className="kcard-meta">
        <Divisions ids={r.divisions} />
      </div>
      <div className="kcard-foot">
        <span>{r.requesterName}</span>
        <span>{r.estHours != null ? `${r.estHours}h - ` : ''}{stuck ? `${daysInStage(r)}d in stage` : ageLabel(r.createdAt)}</span>
      </div>
      {r.waitingOn && <div className="kcard-wait">Waiting: {r.waitingOn}</div>}
      {extra}
    </div>
  );
}
